<template>
  <div
    v-if="userPoints"
    id="profile-points-section"
    class="profile-points-section"
  >
    <!-- Points Modal (used for missions, rank milestones, badge, and thematic event) -->
    <modal
      name="point-system-modal"
      height="auto"
      scrollable
      adaptive
      :click-to-close="false"
    >
      <div
        class="stick-top-right"
        style="top: 10px;"
      >
        <button
          class="btn-close md-close vs-btn-legacy"
          @click="_hideModal"
        />
      </div>
      <div class="container pt-6 pb-4">
        <div class="row">
          <div class="col p-0">
            <component
              :is="currentModal"
              v-if="currentModal"
              @go-to="goTo"
            />
            <div
              v-else
              class="p-6 text-center"
            >
              Loading...
            </div>
          </div>
        </div>
      </div>
    </modal>
    <!-- Points Modal (used for missions, rank milestones, and thematic event) -->

    <div
      id="profile-points-level-badges-section"
      class="d-flex flex-column flex-grow-1"
    >
      <div
        v-if="isOwner"
        class="d-flex flex-column"
      >
        <div class="d-flex align-items-center mr-4 mb-3">
          <div
            id="point-icon"
            class="point-icon"
          >
            P
          </div>
          <span class="mr-4 font-weight-bold">{{ userPoints.experiencePoints.current }}</span>
          <span>{{ userPoints.level.earnMessage }}</span>
        </div>
        <div
          id="progress-bar-wrapper"
          class="d-flex flex-grow-1 align-items-center mb-4"
        >
          <div class="progress mr-4">
            <div
              class="progress-bar"
              role="progressbar"
              :style="{width: `${percentage}%`}"
              :aria-valuenow="percentage"
              aria-valuemin="0"
              aria-valuemax="100"
            />
          </div>
          <span>{{ userPoints.experiencePoints.current }}/{{ userPoints.experiencePoints.requiredForNextLevel }}</span>
        </div>
      </div>
      <div
        id="profile-badges-section"
        class="d-flex flex-column"
      >
        <div
          id="badges__bar"
          class="d-flex"
        >
          <span class="mb-6 mr-3 font-weight-bold">Badges</span>
          <div
            v-if="isOwner"
            @click="_onOpenModal('badge')"
          >
            <img
              src="@/images/right-chevron.png"
              width="14"
              height="14"
              class="cursor-pointer"
            >
          </div>
        </div>
        <div
          v-if="isOwner"
          id="badges-image"
          class="d-flex"
        >
          <img
            v-for="badge in userPoints.badges"
            :key="badge.name"
            class="badge-image"
            :style="!badge.isUnlocked && {backgroundColor: '#a9a9a9', padding: '24px', border:'none'}"
            :src="badgeImage(badge)"
          >
        </div>
        <div
          v-if="!isOwner"
          id="badges-image"
          class="d-flex"
        >
          <img
            v-for="badge in user.points.badges"
            :key="badge.name"
            class="badge-image"
            :style="!badge.isUnlocked && {backgroundColor: '#a9a9a9', padding: '24px', border:'none'}"
            :src="badgeImage(badge)"
          >
        </div>
      </div>
    </div>

    <div
      v-if="isOwner"
      id="profile-points-actions-section"
      class="d-flex mt-4"
    >
      <div
        v-for="action in pointActions"
        :key="action.label"
        class="d-flex position-relative"
      >
        <div
          v-if="action.label === 'Missions' && achievedMissionsCount"
          class="achieved-missions-indicator"
        >
          {{ achievedMissionsCount }}
        </div>
        <VsButton
          class="profile-actions-button"
          @click="action.action"
        >
          {{ action.label }}
        </VsButton>
      </div>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
// Component
import VsProgressBar from '@/scripts/pages/video/Player/PlayerControls/ControlBar/VsProgressBar.vue';
import VsButton from '@/scripts/Components/Molecules/VsButton.vue';
import MissionsListModalContent from './MissionsListModalContent.ts.vue';
import RankMilestonesContent from './RankMilestonesContent.ts.vue';
import ThematicEventContent from './ThematicEventContent.ts.vue';

// Assets
import LockedBadge from '@/images/locked-badge.png';
import { PointSystemStore } from '@/scripts/store/modules/pointSystem';

export default Vue.extend({
  components: {
    VsProgressBar,
    VsButton,
    MissionsListModalContent,
    RankMilestonesContent,
    ThematicEventContent,
  },
  props: {
    user: {
      type: Object,
      default: () => {},
    },
    isOwner: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      currentModal: null,
      pointActions: [
        {
          label: 'Missions',
          action: () => {
            this._onOpenModal('missions');
          },
        },
        {
          label: 'Rank Milestones',
          action: () => {
            this._onOpenModal('milestones');
          },
        },
      ],
    };
  },
  computed: {
    percentage() {
      return this.userPoints.experiencePoints.current/this.userPoints.experiencePoints.requiredForNextLevel*100;
    },
    achievedMissionsCount() {
      return PointSystemStore.state.missionsCount;
    },
    userPoints() {
      return PointSystemStore.state.points;
    },
  },
  methods: {
    badgeImage(badge) {
      return badge.isUnlocked ? badge.listingImage : LockedBadge;
    },
    _hideModal() {
      this.currentModal = null;
      this.$modal.hide('point-system-modal');
    },
    _openModal() {
      this.$modal.show('point-system-modal');
    },

    async _onOpenModal(modalName) {
      this.$modal.show('point-system-modal');

      if (this.currentModal == null) {
        if (modalName === 'missions') {
          this.currentModal = await import('@/scripts/Components/Organisms/Profile/ProfilePointsSection/MissionsListModalContent.ts.vue').then(m => m.default);
        } else if (modalName === 'milestones') {
          this.currentModal = await import('@/scripts/Components/Organisms/Profile/ProfilePointsSection/RankMilestonesContent.ts.vue').then(m => m.default);
        } else if (modalName === 'badge') {
          this.currentModal = await import('@/scripts/Components/Organisms/Profile/ProfilePointsSection/BadgesModalContent.ts.vue').then(m => m.default);
        }
      }
    },

    goTo(page) {
      this._hideModal();
      this._onOpenModal(page);
    },
  },
});
</script>

<style lang="scss" scoped>
@import '~@/stylesheets/exports/_component-entry';

.profile-points-section {
  overflow: hidden;
  display: flex;
  padding: 24px;
  background-color: #eeeeee;
  border-radius: 4px;
  flex-direction: column;
}

.progress {
  display: flex;
  flex-grow: 1;
  height: $progress-height;
  overflow: hidden; // force rounded corners by cropping it
  font-size: $progress-font-size;
  background-color: lightgray;
  max-width: 600px;
  @include border-radius(1rem);
  @include box-shadow($progress-box-shadow);
}

.progress-bar {
  display: flex;
  flex-direction: column;
  justify-content: center;
  color: $progress-bar-color;
  text-align: center;
  background-color: $primary;
  @include transition($progress-bar-transition);
}

.point-icon{
  background-color: $primary;
  color: white;
  border-radius: 50%;
  width: 24px;
  height: 24px;
  display: flex;
  justify-content: center;
  align-items: center;
  margin-right: 8px;
}

.badge-image {
  display: flex;
  padding: 0;
  margin-right: 8px;
  border-radius: 4px;
  width: 80px;
  height: 80px;
  border: 1px solid #343a40
}

.max-progress-width {
  max-width: 300px;
}

.progress-bar-color {
  background-color: $primary;
}

.cursor-pointer {
  cursor: pointer;
}

.profile-actions-button {
  overflow: hidden;
  white-space: nowrap;
  background-color: $primary;
  color: white;
  border-radius: 4px;
  font-size: 16px;

  // Spacing
  padding: 6px 10px;
  margin-right: 12px;

  @media (max-width: 992px){
    font-size: 14px;
  }
}

.achieved-missions-indicator {
  display: flex;
  justify-content: center;
  align-items: center;
  position: absolute;
  top: -8px;
  right: 4px;
  border-radius: 50%;
  width: 20px;
  height: 20px;
  background-color: white;
  color: black;
}

</style>
